import { DatePipe } from '@angular/common'
import { Component, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    FeatureType,
    Grid,
    ModalSize,
    SubcontractorTrainingModule,
    SubcontractorTrainingSendInviteRequest,
    SubcontractorTrainingTrackingRow,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { SubcontractorTrainingDashboardComponent } from './subcontractor-training-dashboard.component'
import { TrackingDetailsModalComponent } from './tracking-details-modal.component'
import { TrainingModuleModalComponent } from './training-module-modal.component'

@Component({
    templateUrl: './subcontractor-training.component.html',
    standalone: true,
    imports: [
        DatePipe,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        SubcontractorTrainingDashboardComponent,
    ],
})
export class SubcontractorTrainingComponent extends BaseComponent {
    @ViewChild(SubcontractorTrainingDashboardComponent)
    dashboard?: SubcontractorTrainingDashboardComponent

    activeTab = 0
    moduleGrid: Grid<SubcontractorTrainingModule>
    trackingGrid: Grid<SubcontractorTrainingTrackingRow>
    availableModules: SubcontractorTrainingModule[] = []
    inviteRequest = new SubcontractorTrainingSendInviteRequest()
    selectedModuleIds: string[] = []
    isSendingInvite = false
    modulesLoaded = false
    trackingLoaded = false

    constructor() {
        super()
        this.moduleGrid = new Grid<SubcontractorTrainingModule>(this.services)
            .setupModalEditing(
                this.services.data.subcontractorTrainingModules,
                'Training Module',
                TrainingModuleModalComponent,
                () => {
                    const module = new SubcontractorTrainingModule()
                    module.isActive = true
                    module.questions = []
                    return module
                },
                this,
                ModalSize.large,
            )
            .filterBy(item => [item.title, item.description])

        this.trackingGrid = new Grid<SubcontractorTrainingTrackingRow>(this.services)
            .setupDeleteOnly(this.services.data.subcontractorTrainingInvites)
            .filterBy(item => [
                item.companyName,
                item.email,
                item.firstName,
                item.lastName,
                item.participantFirstName,
                item.participantLastName,
                item.moduleTitles,
                item.status,
            ])

        this.trackingGrid.itemsDeleted.on(() => {
            this.refreshTracking()
            this.refreshDashboard()
        })

        this.loadAvailableModules()
    }

    tabChanged() {
        if (this.isDashboardTab) {
            this.refreshDashboard()
        }
        if (this.isTrackingTab) {
            this.refreshTracking()
        }
        if (this.isModulesTab && !this.modulesLoaded) {
            this.refreshModules()
        }
    }

    get isDashboardTab() {
        return Number(this.activeTab) === 0
    }

    get isTrackingTab() {
        return Number(this.activeTab) === 1
    }

    get isModulesTab() {
        return Number(this.activeTab) === 2
    }

    loadAvailableModules() {
        this.cancelableRequest(
            'subconModulesList',
            true,
            this.services.data.load(true, true, this.services.data.subcontractorTrainingModules.queryAll()).subscribe(successResult => {
                this.availableModules = successResult[0].filter(x => x.isActive)
            }),
        )
    }

    refreshModules() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.subcontractorTrainingModules.queryAll()).subscribe(
                successResult => {
                    this.modulesLoaded = true
                    this.initialLoadingComplete()
                    this.moduleGrid.items = successResult[0]
                    this.availableModules = successResult[0].filter(x => x.isActive)
                },
                () => {
                    this.modulesLoaded = true
                    this.initialLoadingComplete()
                },
            ),
        )
    }

    refreshTracking() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .get<
                    SubcontractorTrainingTrackingRow[]
                >('/api/SubcontractorTrainingInvite/Tracking', undefined, SubcontractorTrainingTrackingRow)
                .subscribe(
                    rows => {
                        this.trackingLoaded = true
                        this.trackingGrid.items = rows
                    },
                    () => {
                        this.trackingLoaded = true
                    },
                ),
        )
    }

    refreshDashboard() {
        this.dashboard?.refresh()
    }

    onDashboardReady() {
        this.initialLoadingComplete()
    }

    toggleModuleSelection(moduleId: string, checked: boolean) {
        if (checked) {
            if (!this.selectedModuleIds.includes(moduleId)) {
                this.selectedModuleIds.push(moduleId)
            }
        } else {
            this.selectedModuleIds = this.selectedModuleIds.filter(x => x !== moduleId)
        }
    }

    sendInvite() {
        if (!this.inviteRequest.companyName?.trim() || !this.inviteRequest.email?.trim()) {
            this.services.notification.warning('Company name and email are required.')
            return
        }
        if (this.selectedModuleIds.length === 0) {
            this.services.notification.warning('Select at least one training module.')
            return
        }

        this.isSendingInvite = true
        const payload = {
            companyName: this.inviteRequest.companyName.trim(),
            email: this.inviteRequest.email.trim(),
            firstName: this.inviteRequest.firstName?.trim() ?? '',
            lastName: this.inviteRequest.lastName?.trim() ?? '',
            moduleIds: this.selectedModuleIds,
        }

        this.services.data.post('/api/SubcontractorTrainingInvite/SendInvite', payload).subscribe({
            next: () => {
                this.isSendingInvite = false
                this.services.notification.success('Training invite sent.')
                this.inviteRequest = new SubcontractorTrainingSendInviteRequest()
                this.selectedModuleIds = []
                this.refreshTracking()
                this.refreshDashboard()
            },
            error: () => {
                this.isSendingInvite = false
                this.services.notification.error('Failed to send training invite.')
            },
        })
    }

    viewTrackingDetails(row: SubcontractorTrainingTrackingRow) {
        const modalRef = this.services.modals.open(TrackingDetailsModalComponent, ModalSize.large)
        modalRef.componentInstance.row = row
    }

    viewInviteDetailsById(inviteId: string) {
        const openModal = (row: SubcontractorTrainingTrackingRow | undefined) => {
            if (!row) {
                this.services.notification.warning('Unable to load invitation details.')
                return
            }
            this.viewTrackingDetails(row)
        }

        const cachedRow = this.trackingGrid.items.find(x => x.inviteId === inviteId)
        if (cachedRow) {
            openModal(cachedRow)
            return
        }

        this.services.data
            .get<
                SubcontractorTrainingTrackingRow[]
            >('/api/SubcontractorTrainingInvite/Tracking', undefined, SubcontractorTrainingTrackingRow)
            .subscribe({
                next: rows => {
                    this.trackingGrid.items = rows
                    this.trackingLoaded = true
                    openModal(rows.find(x => x.inviteId === inviteId))
                },
                error: () => {
                    this.services.notification.error('Unable to load invitation details.')
                },
            })
    }

    getParticipantName(row: SubcontractorTrainingTrackingRow) {
        const participantName = `${row.participantFirstName ?? ''} ${row.participantLastName ?? ''}`.trim()
        if (participantName) {
            return participantName
        }
        const contactName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim()
        return contactName || '—'
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.SubcontractorTrainingEdit])
    }
}
